import { useId, useState } from 'react';
import './LiksTabs.css';

export type LiksTabItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  content?: React.ReactNode;
};

export type LiksTabsProps = {
  tabs: LiksTabItem[];
  defaultTabId?: string;
  activeTabId?: string;
  onActiveTabIdChange?: (tabId: string) => void;
  'aria-label'?: string;
  tabListTrailing?: React.ReactNode;
};

export function LiksTabs({
  tabs,
  defaultTabId,
  activeTabId: controlledActiveTabId,
  onActiveTabIdChange,
  'aria-label': ariaLabel,
  tabListTrailing,
}: LiksTabsProps) {
  const baseId = useId();
  const [uncontrolledActiveTabId, setUncontrolledActiveTabId] = useState(
    () => defaultTabId ?? tabs[0]?.id ?? '',
  );
  const isControlled = controlledActiveTabId !== undefined;
  const activeTabId = isControlled
    ? controlledActiveTabId
    : uncontrolledActiveTabId;
  const activeTab = tabs.find((tab) => tab.id === activeTabId);

  const setActiveTabId = (tabId: string) => {
    if (!isControlled) {
      setUncontrolledActiveTabId(tabId);
    }
    onActiveTabIdChange?.(tabId);
  };

  return (
    <div className="liks-tabs">
      <div
        className={[
          'liks-tabs__header',
          tabListTrailing != null && 'liks-tabs__header--with-trailing',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="liks-tabs__list" role="tablist" aria-label={ariaLabel}>
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            const tabId = `${baseId}-tab-${tab.id}`;
            const panelId = `${baseId}-panel-${tab.id}`;

            return (
              <button
                key={tab.id}
                type="button"
                id={tabId}
                className={`liks-tabs__tab${isActive ? ' liks-tabs__tab--active' : ''}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={tab.content != null ? panelId : undefined}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTabId(tab.id)}
              >
                {tab.icon}
                <span className="liks-tabs__tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
        {tabListTrailing}
      </div>
      {activeTab?.content != null && (
        <div
          id={`${baseId}-panel-${activeTab.id}`}
          className="liks-tabs__panel"
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${activeTab.id}`}
        >
          {activeTab.content}
        </div>
      )}
    </div>
  );
}
